import api from './api'
import type { Deal, Evidence, DealStage, StageJudgement, StageCompletion, ChangeAlert } from '@/types'

/**
 * Deal API 服务
 */
export const dealService = {
  /**
   * 获取商机列表
   */
  async listDeals(params?: {
    page?: number
    pageSize?: number
    stage?: string
    ownerId?: string
  }) {
    const response = await api.get('/deals', { params })
    return response.data
  },

  /**
   * 获取商机的关键人列表（从证据中聚合）
   * 
   * 注意：使用 /deals/{id}/key-persons 而非 /persons/deal/{id}
   * 前者从证据的 extracted_tags.persons 中聚合，后者需要手动创建 Person 记录
   */
  async getDealPersons(dealId: string) {
    const response = await api.get(`/deals/${dealId}/key-persons`)
    return response.data
  },

  /**
   * 获取商机详情
   */
  async getDeal(dealId: string) {
    const response = await api.get(`/deals/${dealId}`)
    return response.data as Deal
  },

  /**
   * 创建商机
   */
  async createDeal(data: {
    name: string
    stage: string  // 必填：项目当前阶段
    customerName?: string
    industry?: string
    amountEst?: number
    customizationLevel?: string
    procurementType?: string
    expectedCloseDate?: string
    notes?: string
  }) {
    // 手动映射 camelCase → snake_case（因为请求拦截器不做自动转换）
    const requestBody: Record<string, unknown> = {
      name: data.name,
      stage: data.stage,  // 必填字段
    }
    
    if (data.customerName !== undefined) {
      requestBody.customer_name = data.customerName
    }
    if (data.industry !== undefined) {
      requestBody.industry = data.industry
    }
    if (data.amountEst !== undefined) {
      requestBody.amount_est = data.amountEst
    }
    if (data.customizationLevel !== undefined) {
      requestBody.customization_level = data.customizationLevel
    }
    if (data.procurementType !== undefined) {
      requestBody.procurement_type = data.procurementType
    }
    if (data.expectedCloseDate !== undefined) {
      requestBody.expected_close_date = data.expectedCloseDate
    }
    if (data.notes !== undefined) {
      requestBody.notes = data.notes
    }
    
    const response = await api.post('/deals', requestBody)
    return response.data
  },

  /**
   * 更新商机
   */
  async updateDeal(dealId: string, data: Partial<Deal>) {
    const response = await api.put(`/deals/${dealId}`, data)
    return response.data
  },

  /**
   * 删除商机
   */
  async deleteDeal(dealId: string) {
    const response = await api.delete(`/deals/${dealId}`)
    return response.data
  },
}

/**
 * Evidence API 服务
 */
export const evidenceService = {
  /**
   * 获取商机的证据列表
   */
  async listEvidences(dealId: string, params?: {
    sourceType?: string
    status?: string
  }) {
    const response = await api.get(`/evidences/deal/${dealId}`, { params })
    return response.data
  },

  /**
   * 创建证据（文本输入）
   */
  async createEvidence(data: {
    dealId: string
    sourceType: string
    contentRaw: string
    occurredAt?: string
    sourceFileName?: string
  }) {
    const response = await api.post('/evidences', {
      deal_id: data.dealId,
      source_type: data.sourceType,
      content_raw: data.contentRaw,
      occurred_at: data.occurredAt,
      source_file_name: data.sourceFileName,
    })
    return response.data as Evidence
  },

  /**
   * 上传证据文件
   */
  async uploadEvidence(data: {
    dealId: string
    sourceType: string
    file: File
  }) {
    const formData = new FormData()
    formData.append('deal_id', data.dealId)
    formData.append('source_type', data.sourceType)
    formData.append('file', data.file)
    
    const response = await api.post('/evidences/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  },

  /**
   * 获取证据详情
   */
  async getEvidence(evidenceId: string) {
    const response = await api.get(`/evidences/${evidenceId}`)
    return response.data as Evidence
  },

  /**
   * 确认证据
   */
  async confirmEvidence(evidenceId: string) {
    const response = await api.put(`/evidences/${evidenceId}`, {
      status: 'confirmed',
    })
    return response.data
  },

  /**
   * 纠正证据
   */
  async correctEvidence(evidenceId: string, correction: Record<string, unknown>) {
    const response = await api.put(`/evidences/${evidenceId}`, {
      user_correction: correction,
      status: 'corrected',
    })
    return response.data
  },

  /**
   * 标记为不适用
   */
  async markNotApplicable(evidenceId: string, reason?: string) {
    const response = await api.patch(`/evidences/${evidenceId}/mark-not-applicable`, null, {
      params: { reason },
    })
    return response.data
  },

  /**
   * 更新证据的抽取标签
   */
  async updateEvidenceTags(evidenceId: string, data: {
    extractedTags: {
      persons?: Array<{ name: string; role?: string; attitude?: string }>
      facts?: Array<{ category: string; content: string }>
      riskFlags?: string[]
    }
  }) {
    const response = await api.patch(`/evidences/${evidenceId}/tags`, {
      extracted_tags: {
        persons: data.extractedTags.persons,
        facts: data.extractedTags.facts,
        risk_flags: data.extractedTags.riskFlags
      }
    })
    return response.data
  },

  /**
   * 更新证据摘要
   */
  async updateEvidenceSummary(evidenceId: string, summary: string) {
    const response = await api.patch(`/evidences/${evidenceId}/summary`, {
      content_summary: summary
    })
    return response.data
  },
}

/**
 * Diagnosis API 服务
 */
export const diagnosisService = {
  /**
   * 获取体检卡
   */
  async getHealthCard(dealId: string) {
    const response = await api.get(`/diagnosis/deal/${dealId}/health-card`)
    return response.data
  },

  /**
   * 获取全景诊断
   */
  async getFullDiagnosis(dealId: string) {
    const response = await api.get(`/diagnosis/deal/${dealId}/full`)
    return response.data
  },

  /**
   * 刷新诊断
   */
  async refreshDiagnosis(dealId: string) {
    const response = await api.post(`/diagnosis/deal/${dealId}/refresh`)
    return response.data
  },
}

/**
 * Hypothesis API 服务
 */
export const hypothesisService = {
  /**
   * 获取按维度分组的假设
   */
  async getHypothesesByDeal(dealId: string) {
    const response = await api.get(`/hypotheses/deal/${dealId}`)
    return response.data
  },

  /**
   * 初始化假设树
   */
  async initializeHypotheses(dealId: string) {
    const response = await api.post(`/hypotheses/deal/${dealId}/initialize`)
    return response.data
  },

  /**
   * 更新假设状态
   */
  async updateHypothesis(hypothesisId: string, data: {
    status: string
    reasoning?: string
  }) {
    const response = await api.put(`/hypotheses/${hypothesisId}`, data)
    return response.data
  },

  /**
   * 确认假设
   */
  async confirmHypothesis(hypothesisId: string, reasoning?: string) {
    const response = await api.patch(`/hypotheses/${hypothesisId}/confirm`, null, {
      params: { reasoning },
    })
    return response.data
  },

  /**
   * 证伪假设
   */
  async falsifyHypothesis(hypothesisId: string, reasoning?: string) {
    const response = await api.patch(`/hypotheses/${hypothesisId}/falsify`, null, {
      params: { reasoning },
    })
    return response.data
  },

  /**
   * 获取假设关联的证据列表
   */
  async getHypothesisEvidences(hypothesisId: string) {
    const response = await api.get(`/hypotheses/${hypothesisId}/evidences`)
    return response.data
  },

  /**
   * 获取假设关联的动作列表（按等级分组）
   */
  async getHypothesisActions(hypothesisId: string) {
    const response = await api.get(`/hypotheses/${hypothesisId}/actions`)
    return response.data
  },

  // ============================================
  // 冲突通知 API（TASK-H5）
  // ============================================

  /**
   * 获取商机的冲突通知列表
   * 
   * 当自动流程（LLM诊断、信号观测）因终态锁定被拦截时产生冲突通知
   */
  async getDealConflicts(dealId: string, includeResolved = false) {
    const response = await api.get(`/hypotheses/deal/${dealId}/conflicts`, {
      params: { include_resolved: includeResolved }
    })
    return response.data
  },

  /**
   * 获取假设的冲突通知列表
   */
  async getHypothesisConflicts(hypothesisId: string, includeResolved = false) {
    const response = await api.get(`/hypotheses/${hypothesisId}/conflicts`, {
      params: { include_resolved: includeResolved }
    })
    return response.data
  },

  /**
   * 处理冲突通知
   * 
   * @param conflictId 冲突ID
   * @param resolution 处理决策: 'keep' 维持原状态, 'rollback_and_update' 采纳建议
   * @param reasoning 处理原因说明（可选）
   */
  async resolveConflict(conflictId: string, resolution: 'keep' | 'rollback_and_update', reasoning?: string) {
    const response = await api.post(`/hypotheses/conflicts/${conflictId}/resolve`, {
      resolution,
      reasoning
    })
    return response.data
  },
}

/**
 * Action API 服务
 */
export const actionService = {
  /**
   * 获取分级动作建议（只读，不触发生成）
   * 
   * 此 API 仅读取数据库中已持久化的动作，不会触发 LLM 调用。
   * 动作生成只在证据确认时触发。
   * 
   * @param diagnosisType - 可选，不传则返回该 Deal 下所有假设的动作
   */
  async getActionsByGrade(dealId: string, diagnosisType?: string, _urgencyDays?: number) {
    const response = await api.get(`/actions/deal/${dealId}/by-grade`, {
      params: diagnosisType ? { diagnosis_type: diagnosisType } : {}
    })
    return response.data
  },

  /**
   * 手动生成分级动作建议（触发 LLM）
   * 
   * 仅在用户明确请求时调用
   */
  async generateActionsByGrade(dealId: string, diagnosisType: string, urgencyDays?: number) {
    const response = await api.post(`/actions/deal/${dealId}/generate`, {
      diagnosis_type: diagnosisType,
      deal_urgency_days: urgencyDays,
    })
    return response.data
  },

  /**
   * 获取按假设（风险项）分组的动作列表
   * 
   * 用于"建议动作"页面，按风险项展示 A/B/C 分级动作
   * 
   * @param dealId 商机ID
   * @param autoGenerate 是否自动生成缺失的动作（默认 false，避免 LLM 超时）
   */
  async getActionsByHypothesis(dealId: string, autoGenerate = false) {
    const response = await api.get(`/actions/deal/${dealId}/by-hypothesis`, {
      params: { auto_generate: autoGenerate }
    })
    return response.data
  },

  /**
   * 获取商机的动作列表
   */
  async listActionsByDeal(dealId: string, grade?: string, status?: string) {
    const response = await api.get(`/actions/deal/${dealId}`, {
      params: { grade, status },
    })
    return response.data
  },

  /**
   * 更新动作状态
   */
  async updateActionStatus(actionId: string, status: string) {
    const response = await api.patch(`/actions/${actionId}/status`, {
      status,
    })
    return response.data
  },

  /**
   * 采纳动作
   */
  async acceptAction(actionId: string) {
    return actionService.updateActionStatus(actionId, 'accepted')
  },

  /**
   * 标记动作完成
   */
  async completeAction(actionId: string) {
    return actionService.updateActionStatus(actionId, 'done')
  },

  /**
   * 拒绝动作
   */
  async rejectAction(actionId: string, _reason?: string) {
    // reason参数保留用于未来扩展
    return actionService.updateActionStatus(actionId, 'rejected')
  },

  /**
   * 标记动作为不适用
   */
  async markActionNotApplicable(actionId: string, reason?: string) {
    const response = await api.patch(`/actions/${actionId}/mark-not-applicable`, null, {
      params: { reason },
    })
    return response.data
  },

  // ============ 信号相关 API ============

  /**
   * 获取商机级别的所有信号
   * 用于概览页面的信号追踪面板
   */
  async getDealSignals(dealId: string, options?: {
    includeObserved?: boolean
    strengthFilter?: 'Strong' | 'Medium' | 'Weak'
  }) {
    const response = await api.get(`/actions/deal/${dealId}/signals`, {
      params: {
        include_observed: options?.includeObserved ?? true,
        strength_filter: options?.strengthFilter,
      },
    })
    return response.data
  },

  /**
   * 获取动作的预期信号
   */
  async getActionSignals(actionId: string) {
    const response = await api.get(`/actions/${actionId}/signals`)
    return response.data
  },

  /**
   * 标记信号已观测
   */
  async observeSignal(signalId: string, options?: {
    evidenceId?: string
    notes?: string
  }) {
    const response = await api.patch(`/actions/signals/${signalId}/observe`, null, {
      params: {
        evidence_id: options?.evidenceId,
        notes: options?.notes,
      },
    })
    return response.data
  },

  /**
   * 取消信号观测标记
   * 
   * 注意：取消观测不会回滚假设状态（需走人工复核流程）
   */
  async unobserveSignal(signalId: string) {
    const response = await api.patch(`/actions/signals/${signalId}/unobserve`)
    return response.data
  },

  // ============ 待办事项相关 API ============

  /**
   * 获取商机的待办事项列表（已采纳的动作）
   */
  async getTodoList(dealId: string) {
    const response = await api.get(`/actions/deal/${dealId}/todos`)
    return response.data
  },

  /**
   * 更新动作的计划执行日期
   */
  async updateScheduledDate(actionId: string, scheduledDate: string | undefined) {
    const response = await api.patch(`/actions/${actionId}/scheduled-date`, {
      scheduled_date: scheduledDate || null,
    })
    return response.data
  },

  /**
   * 采纳动作并设置执行日期
   */
  async acceptActionWithDate(actionId: string, scheduledDate?: string) {
    // 先采纳
    await actionService.acceptAction(actionId)
    // 再设置日期（如果有）
    if (scheduledDate) {
      await actionService.updateScheduledDate(actionId, scheduledDate)
    }
    return { success: true }
  },
}

/**
 * Evidence关联查询服务
 */
export const evidenceRelationService = {
  /**
   * 获取证据影响的假设列表
   */
  async getEvidenceHypotheses(evidenceId: string) {
    const response = await api.get(`/evidences/${evidenceId}/hypotheses`)
    return response.data
  },
}

/**
 * Stage API 服务
 * 阶段判定与覆盖相关接口
 */
export const stageService = {
  /**
   * 获取阶段判定结果
   * 包含自动推断阶段、最终阶段、置信度等
   */
  async getStageJudgement(dealId: string): Promise<StageJudgement> {
    const response = await api.get(`/deals/${dealId}/stage-judgement`)
    return response.data
  },

  /**
   * 手动覆盖阶段
   * @param dealId 商机ID
   * @param data 覆盖参数
   */
  async overrideStage(dealId: string, data: {
    stage: DealStage
    reason?: string
    expiresAt?: string
  }): Promise<StageJudgement> {
    const response = await api.post(`/deals/${dealId}/stage-override`, {
      stage_override: data.stage,
      reason: data.reason,
      expires_at: data.expiresAt,
    })
    return response.data
  },

  /**
   * 取消阶段覆盖，恢复AI自动推断
   */
  async cancelStageOverride(dealId: string): Promise<StageJudgement> {
    const response = await api.delete(`/deals/${dealId}/stage-override`)
    return response.data
  },

  /**
   * 获取阶段完成度
   * 包含当前阶段的必须确认/必须证伪/观察假设的完成统计
   */
  async getStageCompletion(dealId: string): Promise<StageCompletion> {
    const response = await api.get(`/deals/${dealId}/stage-completion`)
    return response.data
  },
}

/**
 * Change Alert API 服务
 * 变更预警相关接口
 */
export const changeService = {
  /**
   * 获取变更预警列表
   * BR-T11-04: 只返回未忽略的预警
   * @param dealId 商机ID
   * @param includesDismissed 是否包含已忽略的预警（默认 false）
   */
  async getChangeAlerts(dealId: string, includesDismissed = false): Promise<ChangeAlert[]> {
    const response = await api.get(`/changes/deal/${dealId}/alerts`, {
      params: { include_dismissed: includesDismissed },
    })
    return response.data.items || []
  },

  /**
   * 忽略预警
   * BR-T11-05: 使用 PATCH 方法
   * @param alertId 预警ID
   * @param reason 忽略原因（可选）
   */
  async dismissAlert(alertId: string, reason?: string): Promise<ChangeAlert> {
    const response = await api.patch(`/changes/alerts/${alertId}/dismiss`, {
      dismiss_reason: reason,
    })
    return response.data
  },

  /**
   * 获取变更历史
   * @param dealId 商机ID
   * @param params 查询参数
   */
  async getChangeHistory(dealId: string, params?: {
    type?: string
    startDate?: string
    endDate?: string
    limit?: number
  }): Promise<ChangeAlert[]> {
    const response = await api.get(`/changes/deal/${dealId}/history`, { params })
    return response.data.items || []
  },
}

/**
 * DemandLayer API 服务
 * 三层需求（T1模板）相关接口
 */
export const demandLayerService = {
  /**
   * 获取商机的三层需求
   * 返回固定3条记录：organization/personal/solution
   * @param dealId 商机ID
   */
  async getDealDemandLayers(dealId: string) {
    const response = await api.get(`/demand-layers/deal/${dealId}`)
    return response.data
  },

  /**
   * 更新某一层的需求
   * @param dealId 商机ID
   * @param layer 需求层级 (organization/personal/solution)
   * @param data 更新数据
   */
  async updateDemandLayer(dealId: string, layer: string, data: {
    statement?: string
    evidenceIds?: string[]
    gapDescription?: string
    nextQuestion?: string
    status?: string
  }) {
    // 转换为 snake_case
    const requestBody: Record<string, unknown> = {}
    if (data.statement !== undefined) requestBody.statement = data.statement
    if (data.evidenceIds !== undefined) requestBody.evidence_ids = data.evidenceIds
    if (data.gapDescription !== undefined) requestBody.gap_description = data.gapDescription
    if (data.nextQuestion !== undefined) requestBody.next_question = data.nextQuestion
    if (data.status !== undefined) requestBody.status = data.status

    const response = await api.put(`/demand-layers/deal/${dealId}/${layer}`, requestBody)
    return response.data
  },
}
